// src/Dashboard/pages/user/Boutique/components/BoutiqueArticleDrawer.jsx

import React, { useEffect } from "react";
import { FaCartPlus, FaTimes } from "react-icons/fa";

import {
  FALLBACK_ARTICLE_IMAGE,
  formatEUR,
  getArticleImage,
  getStockBadgeClass,
  getStockLabel,
  isOutOfStock,
} from "../utils/boutiqueUtils";

export default function BoutiqueArticleDrawer({
  isOpen,
  article,
  onClose,
  onAdd,
}) {
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !article) return null;

  const image = getArticleImage(article);
  const out = isOutOfStock(article);

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              Détail article
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              {article.nom || "Article"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
              <img
                src={image}
                alt={article.nom || "Article"}
                className="max-h-[620px] w-full object-contain"
                onError={(event) => {
                  event.currentTarget.src = FALLBACK_ARTICLE_IMAGE;
                }}
              />
            </div>

            <div className="space-y-4">
              <div className="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Prix
                </p>

                <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">
                  {formatEUR(article.prix)}
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {article.categorie && (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                      {article.categorie}
                    </span>
                  )}

                  {article.taille && (
                    <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                      Taille : {article.taille}
                    </span>
                  )}

                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ring-1 ${getStockBadgeClass(
                      article
                    )}`}
                  >
                    {getStockLabel(article)}
                  </span>
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Description
                </p>

                <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {article.description || "Aucune description disponible."}
                </p>
              </div>

              <button
                type="button"
                onClick={() => onAdd?.(article)}
                disabled={out}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-5 py-4 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaCartPlus />
                {out ? "Stock épuisé" : "Ajouter au panier"}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}